import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { FeatureCollection, Point } from "geojson";
import type { CellTuple, LensStats, Metric } from "./types";

const LENS_RADIUS = 86;
const EMPTY_STATS: LensStats = {
  records: 0,
  parcels: 0,
  value: 0,
  overFloor: 0,
  bands: [0, 0, 0, 0, 0, 0],
  boroughs: [0, 0, 0, 0, 0],
  classes: [0, 0],
  cellCount: 0
};

const baseStyle: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }
  },
  layers: [
    {
      id: "paper",
      type: "background",
      paint: { "background-color": "#e8e0cf" }
    },
    {
      id: "osm",
      type: "raster",
      source: "osm",
      paint: {
        "raster-saturation": -1,
        "raster-contrast": 0.18,
        "raster-brightness-min": 0.56,
        "raster-brightness-max": 0.96,
        "raster-opacity": 0.54
      }
    }
  ]
};

function asGeoJSON(cells: CellTuple[]): FeatureCollection<Point> {
  return {
    type: "FeatureCollection",
    features: cells.map((cell, index) => ({
      type: "Feature",
      id: index,
      geometry: { type: "Point", coordinates: [cell[0], cell[1]] },
      properties: {
        records: cell[2],
        parcels: cell[3],
        value: cell[4],
        overFloor: cell[5]
      }
    }))
  };
}

function getPaint(metric: Metric) {
  const field = ["get", metric] as ["get", string];
  const scale =
    metric === "value"
      ? [10_000_000, 250_000_000, 1_500_000_000, 7_000_000_000]
      : metric === "overFloor"
        ? [1, 8, 45, 250]
        : [1, 60, 300, 1500];

  return {
    "circle-radius": [
      "interpolate",
      ["linear"],
      field,
      scale[0],
      2,
      scale[1],
      4.5,
      scale[2],
      8,
      scale[3],
      14
    ] as maplibregl.ExpressionSpecification,
    "circle-color": [
      "interpolate",
      ["linear"],
      field,
      scale[0],
      "#ffdc64",
      scale[1],
      "#ff9f43",
      scale[2],
      "#fb4f4f",
      scale[3],
      "#9d1534"
    ] as maplibregl.ExpressionSpecification,
    "circle-opacity": 0.74,
    "circle-stroke-width": 0.7,
    "circle-stroke-color": "#fff5d6"
  };
}

interface Props {
  cells: CellTuple[];
  metric: Metric;
  onLensChange: (stats: LensStats) => void;
}

export function MapCanvas({ cells, metric, onLensChange }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [lens, setLens] = useState({ x: 54, y: 45 });
  const [dragging, setDragging] = useState(false);
  const geojson = useMemo(() => asGeoJSON(cells), [cells]);

  const updateLensStats = useCallback(
    (nextLens = lens) => {
      const map = mapRef.current;
      const container = containerRef.current;
      if (!map || !container) return;

      const bounds = container.getBoundingClientRect();
      const center = {
        x: (nextLens.x / 100) * bounds.width,
        y: (nextLens.y / 100) * bounds.height
      };
      const stats: LensStats = {
        ...EMPTY_STATS,
        bands: [...EMPTY_STATS.bands],
        boroughs: [...EMPTY_STATS.boroughs],
        classes: [...EMPTY_STATS.classes]
      };

      for (const cell of cells) {
        const point = map.project([cell[0], cell[1]]);
        const dx = point.x - center.x;
        const dy = point.y - center.y;
        if (dx * dx + dy * dy > LENS_RADIUS * LENS_RADIUS) continue;
        stats.records += cell[2];
        stats.parcels += cell[3];
        stats.value += cell[4];
        stats.overFloor += cell[5];
        stats.cellCount += 1;
        cell[6].forEach((value, index) => (stats.bands[index] += value));
        cell[7].forEach((value, index) => (stats.boroughs[index] += value));
        cell[8].forEach((value, index) => (stats.classes[index] += value));
      }
      onLensChange(stats);
    },
    [cells, lens, onLensChange]
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle,
      center: [-73.96, 40.715],
      zoom: 9.45,
      minZoom: 8.4,
      maxZoom: 15,
      attributionControl: false
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");
    mapRef.current = map;

    map.on("load", () => {
      map.addSource("roll", { type: "geojson", data: geojson });
      map.addLayer({
        id: "roll-glow",
        type: "circle",
        source: "roll",
        paint: {
          ...getPaint(metric),
          "circle-radius": [
            "+",
            (getPaint(metric)["circle-radius"] as maplibregl.ExpressionSpecification),
            5
          ],
          "circle-opacity": 0.12,
          "circle-blur": 0.8,
          "circle-stroke-width": 0
        }
      });
      map.addLayer({
        id: "roll-points",
        type: "circle",
        source: "roll",
        paint: getPaint(metric)
      });
      updateLensStats();
    });
    map.on("moveend", () => updateLensStats());

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [geojson, metric, updateLensStats]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer("roll-points")) return;
    const paint = getPaint(metric);
    for (const layer of ["roll-points", "roll-glow"]) {
      map.setPaintProperty(layer, "circle-color", paint["circle-color"]);
      if (layer === "roll-points") {
        map.setPaintProperty(layer, "circle-radius", paint["circle-radius"]);
      } else {
        map.setPaintProperty(layer, "circle-radius", [
          "+",
          paint["circle-radius"] as maplibregl.ExpressionSpecification,
          5
        ]);
      }
    }
  }, [metric]);

  useEffect(() => {
    const source = mapRef.current?.getSource("roll") as GeoJSONSource | undefined;
    source?.setData(geojson);
  }, [geojson]);

  function setFromPointer(clientX: number, clientY: number) {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const next = {
      x: Math.max(8, Math.min(92, ((clientX - bounds.left) / bounds.width) * 100)),
      y: Math.max(12, Math.min(88, ((clientY - bounds.top) / bounds.height) * 100))
    };
    setLens(next);
    updateLensStats(next);
  }

  function nudgeLens(event: React.KeyboardEvent<HTMLButtonElement>) {
    const amount = event.shiftKey ? 4 : 1.5;
    const delta = {
      ArrowLeft: [-amount, 0],
      ArrowRight: [amount, 0],
      ArrowUp: [0, -amount],
      ArrowDown: [0, amount]
    }[event.key];
    if (!delta) return;
    event.preventDefault();
    const next = {
      x: Math.max(8, Math.min(92, lens.x + delta[0])),
      y: Math.max(12, Math.min(88, lens.y + delta[1]))
    };
    setLens(next);
    updateLensStats(next);
  }

  return (
    <div className="map-stage" ref={containerRef}>
      <div className="map-instruction">
        <span className="pulse-dot" aria-hidden="true" />
        Drag the spotlight. Zoom the city.
      </div>
      <button
        className={`lens ${dragging ? "is-dragging" : ""}`}
        style={{ left: `${lens.x}%`, top: `${lens.y}%` }}
        aria-label="Map spotlight. Drag it, or use arrow keys, to inspect a different area."
        onKeyDown={nudgeLens}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
          setFromPointer(event.clientX, event.clientY);
        }}
        onPointerMove={(event) => {
          if (dragging) setFromPointer(event.clientX, event.clientY);
        }}
        onPointerUp={() => setDragging(false)}
        onPointerCancel={() => setDragging(false)}
      >
        <span className="lens-crosshair" aria-hidden="true" />
        <span className="lens-label">LOOK HERE</span>
      </button>
    </div>
  );
}
